import { publish } from 'js/actions';
import Menu from 'js/components/elements/menu/Menu';
import {
  DELETE_FLOW_TRIGGERS_REQUEST,
  PUBLISH_RAG_FLOW_REQUEST,
  REFRESH_PIPELINE_REQUEST,
} from 'js/constants/actionTypes';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import useRagRetrieverId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagRetrieverId';
import { ReactComponent as DeleteIcon } from 'new/components/icons/close-cross.svg';
import useFlowTriggersAdapter from 'new/state/flowTriggers/adapter/useFlowTriggersAdapter';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import React from 'react';

interface RetrieverActionsProps {
  data: any;
  type: 'nexset' | 'source';
  horizontalDots?: boolean;
}

export const RetrieverActions: React.FC<RetrieverActionsProps> = ({
  data,
  type,
  horizontalDots = true,
}) => {
  const ragFlowId = useRagFlowId();
  const ragRetrieverId = useRagRetrieverId();
  const { refreshPipelineById, publishPipeline } = usePipelinesAdapter();
  const { deleteLinks, createLinks } = useFlowTriggersAdapter();
  const { draftState, draftActions, setDraftActions, setDraftState } = useDraftContext();

  const handleDeleteNexset = async () => {
    // if this data set is in draft, remove it from draft
    if (draftState.dataSetsPendingLinking?.includes(data.id)) {
      // remove from draftState
      const newDraftState = {
        ...draftState,
        dataSetsPendingLinking: draftState.dataSetsPendingLinking.filter((id) => id !== data.id),
      };

      if (!newDraftState.dataSetsPendingLinking.length) {
        delete newDraftState.dataSetsPendingLinking;
      }

      setDraftState(newDraftState);

      // remove from draftActions
      const newDraftActions = { ...draftActions };
      const newRetrieverDraftActions = { ...draftActions[ragRetrieverId] };
      const createLinkArgsIndex = newRetrieverDraftActions.actions.findIndex(
        (action) => action.name === createLinks.name,
      );
      const createLinksArgs = newRetrieverDraftActions.args[createLinkArgsIndex];
      const createLinksPayload = createLinksArgs[1];
      const newCreateLinksPayload = {
        ...createLinksPayload,
        retriever_data_sets: createLinksPayload.retriever_data_sets.filter((id) => id !== data.id),
      };

      newRetrieverDraftActions.args[0] = [createLinksArgs[0], newCreateLinksPayload];

      setDraftActions(newDraftActions);
    }
    // else, stage the deletion
    else {
      // Add to draft state for tracking deletion
      const newDraftState = {
        ...draftState,
        dataSetsPendingDeletion: [...(draftState.dataSetsPendingDeletion || []), data.id],
      };
      setDraftState(newDraftState);

      const retrieverDataSetsToDelete = [
        ...new Set([...(draftState.dataSetsPendingDeletion || []), data.id]),
      ];
      const linkedFlowsToDelete = [
        ...new Set([...(draftState.flowsPendingDeletion || []), data.flow_node_id]),
      ];
      const linkedFlowsToLink = (draftState.flowsPendingLinking || []).filter(
        (id) => !linkedFlowsToDelete.includes(id),
      );
      const retrieverDataSetsToLink = (draftState.dataSetsPendingLinking || []).filter(
        (id) => !retrieverDataSetsToDelete.includes(id),
      );
      // Add to draft actions for staged deletion
      const newDraftActions = {
        ...draftActions,
        [ragRetrieverId]: {
          ...draftActions[ragRetrieverId],
          actions: [createLinks, deleteLinks, refreshPipelineById],
          sync: true,
          args: [
            [
              ragFlowId,
              {
                // Only include linked_flows if there are any to link
                ...(linkedFlowsToLink.length ? { linked_flows: linkedFlowsToLink } : {}),
                // Only include retriever_data_sets if there are any to link
                ...(retrieverDataSetsToLink.length
                  ? { retriever_data_sets: retrieverDataSetsToLink }
                  : {}),
              },
            ],
            [
              ragFlowId,
              {
                // Only include linked_flows if there are any to delete
                ...(linkedFlowsToDelete.length ? { linked_flows: linkedFlowsToDelete } : {}),
                // Only include retriever_data_sets if there are any to delete
                ...(retrieverDataSetsToDelete.length
                  ? { retriever_data_sets: retrieverDataSetsToDelete }
                  : {}),
              },
            ],
            [ragFlowId],
          ],
          requestTypes: [
            ...(draftActions[ragRetrieverId]?.requestTypes || []),
            DELETE_FLOW_TRIGGERS_REQUEST,
            REFRESH_PIPELINE_REQUEST,
            PUBLISH_RAG_FLOW_REQUEST,
          ],
        },
      };
      setDraftActions(newDraftActions);
    }
  };

  const handleDeleteSource = async () => {
    const originNodeId = data.origin_node_id;
    // if this link is in draft, remove it from draft
    const inDraft = draftState.flowsPendingLinking?.includes(originNodeId);

    if (inDraft) {
      // remove from draftState
      const newFlowsPendingLinking = draftState.flowsPendingLinking.filter(
        (id) => id !== originNodeId,
      );
      const newDraftState = {
        ...draftState,
        flowLinks: {
          ...draftState.flowLinks,
          [ragRetrieverId]: newFlowsPendingLinking,
        },
        flowsPendingLinking: newFlowsPendingLinking,
      };

      if (!newFlowsPendingLinking.length) {
        delete newDraftState.flowLinks;
        delete newDraftState.flowsPendingLinking;
      }

      setDraftState(newDraftState);

      // remove from draftActions
      const newDraftActions = { ...draftActions[ragRetrieverId] };
      const createLinksArgs = newDraftActions.args[0];
      const createLinksPayload = createLinksArgs[1];
      const newCreateLinksPayload = {
        ...createLinksPayload,
        linked_flows: createLinksPayload.linked_flows.filter((id) => id !== originNodeId),
      };

      newDraftActions.args[0] = [createLinksArgs[0], newCreateLinksPayload];

      setDraftActions({
        ...draftActions,
        [ragRetrieverId]: newDraftActions,
      });
    }

    // Add to draft state for tracking deletion
    const newDraftState = {
      ...draftState,
      flowsPendingDeletion: [...(draftState.flowsPendingDeletion || []), originNodeId],
    };
    setDraftState(newDraftState);

    const linkedFlowsToDelete = [
      ...new Set([...(draftState.flowsPendingDeletion || []), data.flow_node_id]),
    ];
    const retrieverDataSetsToDelete = [
      ...new Set([...(draftState.dataSetsPendingDeletion || []), data.id]),
    ];
    const linkedFlowsToLink = (draftState.flowsPendingLinking || []).filter(
      (id) => !linkedFlowsToDelete.includes(id),
    );
    const retrieverDataSetsToLink = (draftState.dataSetsPendingLinking || []).filter(
      (id) => !retrieverDataSetsToDelete.includes(id),
    );

    // Add to draft actions for staged deletion
    const newDraftActions = {
      ...draftActions,
      [ragRetrieverId]: {
        ...draftActions[ragRetrieverId],
        actions: [createLinks, deleteLinks, refreshPipelineById],
        args: [
          [
            ragFlowId,
            {
              // Only include linked_flows if there are any to link
              ...(linkedFlowsToLink.length ? { linked_flows: linkedFlowsToLink } : {}),
              // Only include retriever_data_sets if there are any to link
              ...(retrieverDataSetsToLink.length
                ? { retriever_data_sets: retrieverDataSetsToLink }
                : {}),
            },
          ],
          [
            ragFlowId,
            {
              // Only include linked_flows if there are any to delete
              ...(linkedFlowsToDelete.length ? { linked_flows: linkedFlowsToDelete } : {}),
              // Only include retriever_data_sets if there are any to delete
              ...(retrieverDataSetsToDelete.length
                ? { retriever_data_sets: retrieverDataSetsToDelete }
                : {}),
            },
          ],
          [ragFlowId],
        ],
        requestTypes: [
          ...(draftActions[ragRetrieverId]?.requestTypes || []),
          DELETE_FLOW_TRIGGERS_REQUEST,
          PUBLISH_RAG_FLOW_REQUEST,
          REFRESH_PIPELINE_REQUEST,
        ],
        sync: true,
      },
    };
    setDraftActions(newDraftActions);
  };

  const handleDelete = () => {
    if (data.type === 'DATA_SET') {
      return handleDeleteNexset();
    } else {
      return handleDeleteSource();
    }
  };

  const menuItems = [
    {
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: handleDelete,
      dialogMessage: `Please confirm that you want to remove this Retriever`,
      primaryButtonColor: 'error',
      discardLabel: 'Cancel',
      confirmLabel: 'Delete',
    },
  ];

  return <Menu horizontalDots={horizontalDots} items={menuItems} />;
};

export default RetrieverActions;
